import { X } from "lucide-react";
import { useEffect } from "react";

import { cn } from "../../../../lib/utils";

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: "default" | "destructive" | "primary";
  isLoading?: boolean;
}

const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Confirm Action",
  message = "Are you sure you want to proceed?",
  confirmText = "Confirm",
  cancelText = "Cancel",
  confirmVariant = "default",
  isLoading = false
}: ConfirmModalProps) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen && !isLoading) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, isLoading, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  const handleConfirm = () => {
    if (!isLoading) {
      onConfirm();
    }
  };

  const getConfirmButtonClasses = () => {
    return cn(
      "tw-inline-flex tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-rounded-lg tw-transition-all tw-duration-200 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-shadow-sm",
      confirmVariant === "destructive" &&
        "tw-bg-destructive tw-text-white hover:tw-bg-destructive/90 focus:tw-ring-destructive/50",
      confirmVariant === "primary" &&
        "tw-bg-primary tw-text-primary-foreground hover:tw-bg-primary/90 focus:tw-ring-primary/50",
      confirmVariant === "default" &&
        "tw-bg-primary tw-text-primary-foreground hover:tw-bg-primary/90 focus:tw-ring-ring/50"
    );
  };

  return (
    <div
      className="tw-fixed tw-inset-0 tw-z-[9999] tw-flex tw-items-center tw-justify-center tw-p-4 tw-bg-black/60 tw-backdrop-blur-sm tw-animate-in tw-fade-in tw-duration-200"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div className="tw-relative tw-w-full tw-max-w-md tw-rounded-lg tw-border tw-border-border tw-bg-background tw-shadow-lg tw-transform tw-animate-in tw-zoom-in-95 tw-slide-in-from-bottom-4 tw-duration-300">
        <div className="tw-flex tw-items-start tw-justify-between tw-gap-4 tw-px-6 tw-pt-6">
          <h3
            id="modal-title"
            className="tw-text-lg tw-font-semibold tw-text-foreground tw-leading-tight tw-pr-8"
          >
            {title}
          </h3>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="tw-absolute tw-top-4 tw-right-4 tw-inline-flex tw-items-center tw-justify-center tw-p-1 tw-text-muted-foreground hover:tw-text-foreground hover:tw-bg-accent tw-rounded-md tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
            aria-label="Close dialog"
            type="button"
          >
            <X className="tw-h-4 tw-w-4" />
          </button>
        </div>

        <div className="tw-px-6 tw-py-4">
          <p className="tw-text-sm tw-text-muted-foreground tw-leading-relaxed">{message}</p>
        </div>

        <div className="tw-flex tw-items-center tw-gap-3 tw-px-6 tw-py-4 tw-border-t tw-border-border">
          <button
            onClick={onClose}
            disabled={isLoading}
            type="button"
            className="tw-inline-flex tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-foreground tw-bg-background tw-border tw-border-border tw-rounded-lg hover:tw-bg-accent focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-ring/50 tw-transition-all tw-duration-200 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-shadow-sm"
          >
            {cancelText}
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className={getConfirmButtonClasses()}
            type="button"
          >
            {isLoading ? (
              <>
                <svg
                  className="tw-animate-spin tw-h-4 tw-w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="tw-opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="tw-opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span>Loading...</span>
              </>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
